/**
 * 長崎県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 農水省「うちの郷土料理」の長崎県は全30品あり、料理はほぼ国の資料だけで固められた。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - ちゃんぽん・皿うどんの店名と創始者名、1899年という年。農水省は断定で書いているが、
 *    本作は店名を書かない方針で、年も店に紐づく。「明治期に長崎で生まれた」までにする。
 *  - ちゃんぽんの語源は農水省が3説を併記（中国語「喰飯」／ポルトガル語／福建料理）。
 *    ひとつに決めない。
 *  - レモンステーキの「1955年に洋食店の兄弟が考案」も店に紐づく発祥譚。
 *    「1955年ごろから佐世保で親しまれている」にとどめる。
 *  - 具雑煮の島原の乱起源、大村寿司の大村純伊の凱旋、五島うどんの遣唐使伝来、
 *    かんころ餅のキリシタン起源は、いずれも出典自身が伝聞形。
 *  - びわの「日本一」は長崎市公式が書いているので出典つきで使えるが、年次や数量は書かない。
 *  - 「茂木びわ」という品種はない。品種は「茂木」「長崎早生」「なつたより」で、茂木は産地名。
 *  - 鯨料理（鯨じゃが）は出典が完全に取れるが、取材や海外からの反応を考えて採用しない。
 *  - 長崎ミルクセーキは枠の都合で外した。農水省に掲載があるので後から入れ替えられる。
 *
 * 本番画像は public/assets/foods/nagasaki/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：かんころ餅・ヒカド・大村寿司・いりやき（店で食べられる品に差し替え）
 * - 五島うどん：「日本三大うどん」の呼び方は、農水省・五島手延うどん協同組合のページで確認できなかったので書かない
 * - 五島うどん：「遣唐使が原型を伝えた」「中国・浙江省の索麺がルーツ」は農水省ページに伝聞・説として載っているが、説が割れているので書かない
 * - 五島うどん：地獄炊きの名の由来（「しごくおいしい」の聞き違い）は公式ページで確認できなかったので書かない
 * - 具雑煮：「天草四郎が考案した」は島原市ページに「とされています」とあるが、人物の考案として言い切れないので書かず、農水省の「始まりといわれる」の形にとどめた
 * - 具雑煮：「約3か月籠城して戦った」は伝承の色が濃いので書かない
 * - 具雑煮：平成19年に農水省「農山漁村の郷土料理百選」に選ばれたこと（島原市ページ）は入れてよい事実だが、文字数の都合で入れなかった
 * - トルコライス：名前の由来は長崎市のページに「諸説あり」とあるので、どの説も書かない
 * - トルコライス：考案した店・考案年（at-nagasaki.jp の店紹介に「昭和33年に考案」とある）は店の主張で自治体が裏付けたものではないので書かない
 * - トルコライス：デミグラスソースは市のページに記述がないので書かない
 * - 品名「対州いりやきそば」を「対州そば（いりやき）」に地元で通じる呼び名に改めた（2026-10-11 地元民レビュー。同じ料理なので ID はそのまま）。農水省ページの見出しは「対州そば」
 * - 対州いりやきそば：対州そばが2018年に地理的表示（GI）に登録されたこと（長崎県初・そばで初）は農水省ページにあるが、品種の話で料理の説明から外れるので入れなかった（入れてよい事実ではある）
 * - 対州いりやきそば：そばが縄文時代に朝鮮半島経由で伝わったとされる話は伝承の色が濃いので書かない
  * - 2026-10-12 地元民レビューで「対州そば（いりやき）」を外し「佐世保バーガー」を入れた（地元の人がまず挙げる品のため。旧IDは idMigrations の retiredItems）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { NAGASAKI_FOOD_HULLS } from './nagasakiFoodShapes.generated';
import { nagasakiSceneryTour } from './nagasakiScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'nagasaki-biwa',
    level: 1,
    name: '長崎びわ',
    shortName: 'びわ',
    image: '/assets/foods/nagasaki/01_nagasaki_biwa.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '江戸時代から、茂木の斜面で。',
    source: 'https://www.city.nagasaki.lg.jp/page/2435.html',
    icon: { label: 'びわ', shape: 'berry', baseColor: '#e8a83a', accentColor: '#8a6a2a' },
    reward: {
      unlockScore: 200,
      area: '長崎市（茂木地区）',
      headline: '江戸時代から茂木地区で育てられてきた果物。長崎市は生産量が日本一と紹介している。',
      tip: '品種は「茂木」「長崎早生」「なつたより」など。茂木は品種名ではなく産地の名前。',
      links: searchLinks('長崎市 茂木', 'びわ'),
    },
  },
  {
    id: 'kanzarashi',
    level: 2,
    name: 'かんざらし',
    shortName: 'かんざらし',
    image: '/assets/foods/nagasaki/02_kanzarashi.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '島原の湧水で、冷やす。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_30_nagasaki.html',
    icon: { label: 'かんざらし', shape: 'mochi', baseColor: '#f6f2ea', accentColor: '#d8b878' },
    reward: {
      unlockScore: 500,
      area: '島原市',
      headline: '島原の湧水で冷やした小さな白玉に、砂糖の蜜をかけた甘味。',
      tip: '文化庁の100年フードに「島原名物かんざらし」として認定されている。',
      links: searchLinks('島原市', 'かんざらし'),
    },
  },
  {
    id: 'castella',
    level: 3,
    name: 'カステラ',
    shortName: 'カステラ',
    image: '/assets/foods/nagasaki/03_castella.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '名は、遠い王国から。',
    source: 'https://www.city.nagasaki.lg.jp/site/kodomo/60691.html',
    icon: { label: 'カステラ', shape: 'mochi', baseColor: '#f0d488', accentColor: '#8a5a2b' },
    reward: {
      unlockScore: 1000,
      area: '長崎市',
      headline: '名はカスティーリャ王国に由来するとされる、長崎に伝わった南蛮菓子。',
      tip: '底にざらめが残るものがある。切り分けて、そのまま食べるのが基本。',
      links: searchLinks('長崎市', 'カステラ'),
    },
  },
  {
    id: 'hatoshi',
    level: 4,
    name: 'ハトシ',
    shortName: 'ハトシ',
    image: '/assets/foods/nagasaki/04_hatoshi.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '食パンで、はさんで揚げる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_29_nagasaki.html',
    icon: { label: 'ハトシ', shape: 'dumpling', baseColor: '#d9a45c', accentColor: '#e07a5a' },
    reward: {
      unlockScore: 1800,
      area: '長崎市',
      headline: 'エビのすり身を食パンではさんで油で揚げたもの。卓袱料理の一品でもある。',
      tip: '揚げたあと食べやすい大きさに切る。魚のすり身やはんぺんを使うこともある。',
      links: searchLinks('長崎市', 'ハトシ'),
    },
  },
  {
    id: 'goto-udon',
    level: 5,
    name: '五島うどん',
    shortName: '五島うどん',
    image: '/assets/foods/nagasaki/05_goto_udon.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '五島列島の古くからの特産のうどん。乾麺を鉄鍋でぐつぐつゆでて戻し、五島沖でとれたトビウオでとったアゴだしで食べる。家庭の料理としても、大勢で鍋を囲む行事の料理としても親しまれてきた。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_12_nagasaki.html',
    icon: { label: '五島うどん', shape: 'noodle-bowl', baseColor: '#f4efe2', accentColor: '#d9a441' },
    reward: {
      unlockScore: 3000,
      area: '五島列島',
      headline: 'アゴだしで食べる細いうどん',
      tip: '手延べでつくる。棒状の生地を2本の箸にかけて引き延ばしては束ね、紐のように細い麺にする。麺どうしが付かないよう、五島特産の食用椿油を使う。',
      links: searchLinks('五島市', '五島うどん'),
      official: [{ label: '五島手延うどん協同組合「美味しい食べ方」', url: 'https://www.goto-udon.jp/recipe/' }],
    },
  },
  {
    id: 'shimabara-guzoni',
    level: 6,
    name: '具雑煮',
    shortName: '具雑煮',
    image: '/assets/foods/nagasaki/06_shimabara_guzoni.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '土鍋に張っただし汁に、丸餅、鶏肉、白菜、ごぼう、干し椎茸、高野豆腐、焼きアナゴ、卵焼きなどを入れて炊き、鍋のまま食べる。全国でも珍しい、土鍋でつくる具だくさんの雑煮として知られる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_1_nagasaki.html',
    icon: { label: '具雑煮', shape: 'hotpot', baseColor: '#e9d3a0', accentColor: '#5f8f3a' },
    reward: {
      unlockScore: 4500,
      area: '島原市ほか島原半島',
      headline: '土鍋で炊く具だくさんの雑煮',
      tip: '1637年の島原の乱で、籠城した信徒たちが兵糧の餅に山と海の幸を合わせて煮て食べたのが始まりといわれる。地元では正月や祭礼の日など、めでたいときに食べる。',
      links: searchLinks('島原市', '具雑煮'),
      official: [{ label: '島原市「具雑煮」', url: 'https://www.city.shimabara.lg.jp/page953.html' }],
    },
  },
  {
    id: 'sara-udon',
    level: 7,
    name: '皿うどん',
    shortName: '皿うどん',
    image: '/assets/foods/nagasaki/07_sara_udon.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '細麺と太麺、どちらも皿うどん。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_7_nagasaki.html',
    icon: { label: '皿うどん', shape: 'ramen', baseColor: '#e0c060', accentColor: '#8a9a5a' },
    reward: {
      unlockScore: 6500,
      area: '長崎市',
      headline: '麺に五目あんをかけた一皿。パリパリの細麺と、焼きちゃんぽんの太麺がある。',
      tip: '長崎独自のウスターソースや酢をかけて食べる習慣がある。',
      links: searchLinks('長崎市', '皿うどん'),
    },
  },
  {
    id: 'champon',
    level: 8,
    name: 'ちゃんぽん',
    shortName: 'ちゃんぽん',
    image: '/assets/foods/nagasaki/08_champon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '中華鍋ひとつで、できあがる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_8_nagasaki.html',
    icon: { label: 'ちゃんぽん', shape: 'ramen', baseColor: '#f0e8d0', accentColor: '#c05a4a' },
    reward: {
      unlockScore: 9000,
      area: '長崎市',
      headline: '肉・海鮮・野菜を炒め、麺を濃いめのスープで煮込んだ一杯。明治期に長崎で生まれた。',
      tip: '具は季節で変わる。アサリは夏、カキは冬。語源には複数の説がある。',
      links: searchLinks('長崎市', 'ちゃんぽん'),
    },
  },
  {
    id: 'toruko-rice',
    level: 9,
    name: 'トルコライス',
    shortName: 'トルコライス',
    image: '/assets/foods/nagasaki/09_toruko_rice.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'ピラフとスパゲッティ、トンカツを一皿に盛り合わせた洋食。量が多く、「大人のお子様ランチ」とも言われる。ちゃんぽん・皿うどんと並ぶ長崎市民のなじみの味とされる。',
    source:
      'https://www.city.nagasaki.lg.jp/kanko/870000/871000/p023276.html',
    icon: { label: 'トルコライス', shape: 'curry-plate', baseColor: '#d9a441', accentColor: '#c0392b' },
    reward: {
      unlockScore: 12000,
      area: '長崎市',
      headline: '三つの洋食を一皿に',
      tip: '名前の由来は諸説ある。店によってステーキやシーフード、オムライスを合わせるなど種類が多く、何種類か頼んで分け合う食べ方もすすめられている。',
      links: searchLinks('長崎市', 'トルコライス'),
      official: [{ label: '長崎市「ご当地グルメ・トルコライス」', url: 'https://www.at-nagasaki.jp/gourmet/special-feature/4582/index.html' }],
    },
  },
  {
    id: 'sasebo-burger',
    level: 10,
    name: '佐世保バーガー',
    shortName: '佐世保バーガー',
    image: '/assets/foods/nagasaki/10_sasebo_burger.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '昭和25年頃、佐世保に駐留していたアメリカ海軍から地元の人々にレシピが伝えられたのが始まりといわれる。日本人の口に合うように佐世保流にアレンジされ、注文を受けてから作るのが特徴。',
    source:
      'https://www.mlit.go.jp/kankocho/cruise/jp/jp/detail/064/documents/kanko.pdf',
    icon: { label: 'バーガー', shape: 'mochi', baseColor: '#d89a50', accentColor: '#4a8a3a' },
    reward: {
      unlockScore: 16000,
      area: '佐世保市',
      headline: '注文を受けてから作るバーガー',
      tip: '観光協会が紹介する店の条件は、注文を受けてから作り始めること、本社が佐世保にあること、新鮮な生野菜とオリジナルソースが入っていること。',
      links: searchLinks('佐世保市', '佐世保バーガー'),
      official: [{ label: '佐世保観光コンベンション協会「佐世保バーガー」まるわかり', url: 'https://www.sasebo99.com/gourmet/burger' }],
    },
  },
  {
    id: 'lemon-steak',
    level: 11,
    name: 'レモンステーキ',
    shortName: 'レモンステーキ',
    image: '/assets/foods/nagasaki/11_lemon_steak.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '締めは、鉄板の上のご飯。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/46_24_nagasaki.html',
    icon: { label: 'レモンステーキ', shape: 'feast', baseColor: '#b8503a', accentColor: '#e8d048' },
    reward: {
      unlockScore: 22000,
      area: '佐世保市',
      headline: '薄切りの牛肉を鉄板で焼き、レモンの効いた醤油だれで食べる。1955年ごろから佐世保で。',
      tip: '肉を食べ終わったら、鉄板にご飯をのせて残った肉汁とソースに絡めるのが地元流。',
      links: searchLinks('佐世保市', 'レモンステーキ'),
    },
  },
  {
    id: 'shippoku',
    level: 12,
    name: '卓袱料理',
    shortName: '卓袱料理',
    image: '/assets/foods/nagasaki/12_shippoku.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '「おひれをどうぞ」から始まる。',
    source: 'https://www.nagasaki-tabinet.com/eat/132',
    icon: { label: '卓袱料理', shape: 'feast', baseColor: '#c0392b', accentColor: '#f0d8b0' },
    reward: {
      unlockScore: 30000,
      area: '長崎市',
      headline: '朱塗りの円卓を囲み、大皿から取り分ける和・華・蘭の饗応料理。',
      tip: '「おひれをどうぞ」の挨拶で始まる。円卓に上座・下座がない。',
      links: searchLinks('長崎市', '卓袱料理'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const nagasakiFoodStage: StageDefinition = {
  regionId: 'nagasaki',
  regionName: '長崎県',
  mode: 'food',
  title: '長崎めしマージ',
  theme: {
    primaryColor: '#8a3a4a',
    secondaryColor: '#d9a441',
    accentColor: '#e8c86b',
    backgroundColor: '#1a1416',
  },
  /** 背景ツアー「長崎めぐり」。軸は時間帯。島から始めて長崎市の夜で終わる。 */
  scenery: {
    tour: nagasakiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, NAGASAKI_FOOD_HULLS),
};
